import type { Job, RoofLayout, WallMeasurement } from "@roof-hawk/core";
import { useState } from "react";

import { Stub } from "../../components/ui";
import { t } from "../../i18n";
import { fmtDateTime } from "../../lib/format";
import {
  WallMeasurementModal,
  type WallMeasurementInput,
} from "./WallMeasurementModal";

/**
 * The Wall Measurements card — the siding's numbers.
 *
 * Sits under the Roof Measurements card rather than inside it: a wall is not a
 * roof layout, nothing that adds up a job's roof should see one, and the form
 * asks for different numbers. Jonathon, 2026-09-30: "there will be different
 * fields for wall measurements than roof measurements."
 */
export function WallMeasurementsCard({
  job,
  walls,
  layouts,
  onSave,
  onDelete,
}: {
  job: Job;
  walls: readonly WallMeasurement[];
  /** The job's roof layouts, so the modal knows the roof is already counted. */
  layouts: readonly RoofLayout[];
  onSave: (input: WallMeasurementInput) => Promise<void>;
  onDelete: (wall: WallMeasurement) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="block">
      <h2>{t("Wall Measurements")}</h2>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button className="btn-primary" onClick={() => setOpen(true)}>
          {walls.length === 0
            ? t("Add wall measurements")
            : t("Add or edit wall measurements")}
        </button>
      </div>

      {walls.length === 0 ? (
        <Stub
          title={t("No wall measurements yet")}
          message={t(
            "For siding. Upload the Hover report and the wall numbers are read off it for you to confirm — and the roof's too, if you want them — or key them in by hand.",
          )}
        />
      ) : (
        <div className="ewrap" style={{ marginTop: 14 }}>
          <table className="etable">
            <thead>
              <tr>
                <th>{t("Walls")}</th>
                <th className="num">{t("Siding sq ft")}</th>
                <th className="num">{t("Waste")}</th>
                <th className="num">{t("Stories")}</th>
                <th>{t("Source")}</th>
                <th>{t("Captured")}</th>
              </tr>
            </thead>
            <tbody>
              {walls.map((wall) => (
                <tr key={wall.id}>
                  <td
                    title={[wall.name || t("Walls"), wall.pdf_filename]
                      .filter(Boolean)
                      .join(" — ")}
                  >
                    <div className="rowline">
                      <span className="an">{wall.name || t("Walls")}</span>
                      {wall.pdf_filename && (
                        <span className="na-hint">{wall.pdf_filename}</span>
                      )}
                    </div>
                  </td>
                  <td className="num">
                    {wall.siding_trim_sqft ?? wall.siding_sqft ?? "—"}
                  </td>
                  <td className="num">
                    {wall.waste_pct == null ? "—" : `${wall.waste_pct}%`}
                  </td>
                  <td className="num">{wall.stories ?? "—"}</td>
                  <td className="sub">
                    {wall.source === "pdf"
                      ? t("Read from report")
                      : t("Keyed in")}
                  </td>
                  <td className="sub">{fmtDateTime(wall.captured_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <WallMeasurementModal
          job={job}
          walls={walls}
          layouts={layouts}
          onClose={() => setOpen(false)}
          onDelete={onDelete}
          onSave={async (input) => {
            await onSave(input);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}
